var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import { getValueKey } from '@juvare/common-form-ui/WidgetForm/Utils';
import { themeColors } from '@juvare/common-web-ui/JuvareTheme';
import { getDataValue, removeDataValue, setDataValue } from '@juvare/json-data-util';
import { Autocomplete, Box, Button, Checkbox, CircularProgress, Divider, ListItem, Paper, TextField, } from '@mui/material';
import React from 'react';
import { useCommonDataTableUiTranslation, usePropChange } from '../../hooks';
import { useValues } from '../hooks';
import { removeValuesFromComponentDependencies } from '../utils/deleteComponentDependencies';
export const QuickFilterMultiSelect = (_a) => {
    var _b;
    var { valueProviders: valueProvidersFromProps, dataPath, label, values, valueProviderType, valueProvider, filterData, onChangeFilterData, sx, filterDependencies } = _a, props = __rest(_a, ["valueProviders", "dataPath", "label", "values", "valueProviderType", "valueProvider", "filterData", "onChangeFilterData", "sx", "filterDependencies"]);
    const [items, loading] = useValues({
        values,
        valueProviderType,
        valueProvider,
        valueProvidersFromProps,
        filterData,
    });
    const [open, setOpen] = React.useState(false);
    const textFieldRef = React.useRef(null);
    const value = getComponentValue(filterData, dataPath);
    const [changedValue, setChangedValue] = React.useState(value);
    const changedValueObjects = items.filter((item) => changedValue.includes(item.value));
    const labelWithChipCount = changedValue.length === 0 ? label : label + ' (' + changedValue.length + ')';
    const valueIsChanged = JSON.stringify(value) !== JSON.stringify(changedValue);
    const handleReset = () => {
        setChangedValue([]);
    };
    const handleCancel = () => {
        setOpen(false);
        setChangedValue(value);
    };
    const handleApply = () => {
        setOpen(false);
        if (JSON.stringify(value) !== JSON.stringify(changedValue)) {
            onChangeFilterData((prevFilterData) => setComponentValue(prevFilterData, dataPath, changedValue, filterDependencies));
        }
    };
    usePropChange(filterData, React.useCallback((newFilterData) => {
        const newValue = getComponentValue(newFilterData, dataPath);
        setChangedValue((prevChangedValue) => {
            if (JSON.stringify(newValue) !== JSON.stringify(prevChangedValue)) {
                return newValue;
            }
            else {
                return prevChangedValue;
            }
        });
    }, [dataPath]));
    return (React.createElement(CustomContext.Provider, { value: {
            value,
            valueIsChanged,
            handleReset,
            handleCancel,
            handleApply,
        } },
        React.createElement(Autocomplete, { open: open, onOpen: () => setOpen(true), onClose: () => setOpen(false), multiple: true, fullWidth: true, size: 'small', value: changedValueObjects, options: items, getOptionKey: (option) => getValueKey(option.value), loading: loading, openOnFocus: true, disableCloseOnSelect: true, PaperComponent: CustomPaperComponent, renderInput: (params) => (React.createElement(TextField, Object.assign({ ref: textFieldRef }, params, { label: '', placeholder: labelWithChipCount, InputProps: Object.assign(Object.assign({}, params.InputProps), { endAdornment: loading ? (React.createElement(React.Fragment, null,
                        React.createElement(CircularProgress, { color: 'inherit', size: 20 }),
                        params.InputProps.endAdornment)) : (params.InputProps.endAdornment) }), sx: {
                    flex: '1 1 0',
                    minWidth: 0,
                    '& input.MuiOutlinedInput-input.MuiAutocomplete-input.MuiInputBase-inputSizeSmall': {
                        // adding 2px to so that height is 42px instead of 40px
                        paddingTop: '6.5px', // magic value = 5.5px (value from JuvareTheme) + 1px
                        paddingBottom: '5.5px', // magic value = 4.5px (value from JuvareTheme) + 1px
                    },
                    '& legend': {
                        width: 0,
                    },
                    '& input::placeholder': {
                        typography: 'body2',
                        color: themeColors.text.subtle,
                        opacity: 1,
                        textOverflow: 'ellipsis',
                    },
                } }))), renderOption: (renderOptionProps, option, { selected }) => (React.createElement(ListItem, Object.assign({}, renderOptionProps),
                React.createElement(Checkbox, { sx: { mr: 1 }, checked: selected }),
                option.label)), renderTags: () => null, onChange: (event, newValue) => {
                var _a, _b;
                if (!open && ((_b = (_a = event.currentTarget) === null || _a === void 0 ? void 0 : _a.classList) === null || _b === void 0 ? void 0 : _b.contains('MuiAutocomplete-clearIndicator'))) {
                    if (JSON.stringify(value) !== JSON.stringify([])) {
                        setChangedValue([]);
                        onChangeFilterData((prevFilterData) => setComponentValue(prevFilterData, dataPath, [], filterDependencies));
                    }
                }
                else {
                    setChangedValue(newValue.map((value) => value.value));
                }
            }, onBlur: () => {
                handleApply();
            }, sx: [
                {
                    flex: '1 1 0',
                    minWidth: 0,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'stretch',
                },
                ...(Array.isArray(sx) ? sx : [sx]),
            ], componentsProps: {
                popper: { placement: 'bottom-start', style: { minWidth: (_b = textFieldRef.current) === null || _b === void 0 ? void 0 : _b.clientWidth, maxWidth: '100%' } },
            }, "data-test": props['data-test'] })));
};
const CustomContext = React.createContext({});
const CustomPaperComponent = (props) => {
    const { t } = useCommonDataTableUiTranslation();
    const { value, valueIsChanged, handleReset, handleCancel, handleApply } = React.useContext(CustomContext);
    return (React.createElement(Paper, Object.assign({}, props),
        React.createElement(Box, null, props.children),
        React.createElement(Box, { onMouseDown: (event) => event.preventDefault(), onMouseUp: (event) => event.preventDefault(), onClick: (event) => event.preventDefault(), "aria-hidden": true },
            React.createElement(Divider, null),
            React.createElement(Box, { sx: { p: 1, display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1 } },
                value != null && !valueIsChanged ? (React.createElement(Button, { onClick: handleReset }, t('reset'))) : (React.createElement(Button, { onClick: handleCancel }, t('cancel'))),
                React.createElement(Button, { variant: 'contained', onClick: handleApply }, t('apply'))))));
};
function getComponentValue(data, dataPath) {
    const value = data ? getDataValue(data, dataPath) : [];
    if (Array.isArray(value)) {
        return value;
    }
    else {
        return [];
    }
}
function setComponentValue(data, dataPath, value, dependencyMap) {
    const newData = JSON.parse(JSON.stringify(data !== null && data !== void 0 ? data : {}));
    if (value.length > 0) {
        setDataValue(newData, dataPath, value);
    }
    else {
        removeDataValue(newData, dataPath);
    }
    removeValuesFromComponentDependencies(newData, dataPath, dependencyMap);
    return newData;
}
